"use client";

import { c as _c } from "react/compiler-runtime";
import * as RadioGroupPrimitives from "@radix-ui/react-radio-group";
import { useQuery } from "@tanstack/react-query";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { IoArrowBack } from "react-icons/io5";
import { LuClipboardList, LuFileText, LuMail, LuMessageSquare } from "react-icons/lu";
import { RiErrorWarningLine } from "react-icons/ri";
import { EmailComposer, type EmailComposerRef } from "@/components/email-composer";
import { createSlashCommands } from "@/components/email-composer/editor/slash-commands";
import type { TiptapEmailJSON } from "@/components/email-composer/types/tiptap-json";
import { useMembershipFormGuard } from "@/components/forms/membership-form-guard";
import PatientSearch from "@/components/patient/patient-search";
import { ServiceSearchSelect } from "@/components/service-search-select";
import { Button } from "@/components/ui";
import { Dialog, DialogBody, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import Input from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { MultiSelect, MultiSelectContent, MultiSelectItem, MultiSelectPills, MultiSelectTrigger } from "@/components/ui/multi-select";
import Switch from "@/components/ui/switch";
import { useCommunication } from "@/contexts/chat-provider";
import { useTenant } from "@/contexts/tenant-provider";
import { useInPersonForm } from "@/hooks/use-in-person-form";
import { buildFormsEmailDocument } from "@/lib/form-email-utils";
import { generateMultipleFormsMessage } from "@/lib/form-message-utils";
import { formatEmailOrHidden, formatPhoneNumberOrHidden, showsEmail, showsPhoneNumber } from "@/lib/format-data";
import { closeDropdownOnEscape, keepOpenWhileDropdownOpen } from "@/lib/nested-dropdown-dismiss";
import { parseError } from "@/lib/parse-error";
import { cx, focusRing } from "@/lib/tailwind-utils";
import { toast } from "@/lib/use-toast";
import { MessageKind } from "@/types/enums/message";
import type { FormTable } from "@/types/forms/form";
import type { PatientSummary } from "@/types/patient";
import type { UserTiny } from "@/types/user";
import { recordFormAssignment, sendFormEmail } from "@/api/forms/form-management";
import { formQueryOptions } from "@/api/forms/form-query";
import { serviceQueryOptions } from "@/api/provider/admin/service-query";
import { useEmailHeader } from "@/api/provider/comms/email/email-query";
import { useSuggestions } from "@/api/query/provider/suggestions";
import { useMessageDraftStore } from "@/stores/message-draft-store";
type SendMethod = "text" | "email" | "in_person";
interface DeliveryOptionProps {
  value: SendMethod;
  icon: React.ReactNode;
  label: string;
  /** Where it goes: the number or address, or why it can't be used. */
  detail: string;
  disabled?: boolean;
}
function DeliveryOption(t0) {
  const $ = _c(18);
  const {
    value,
    icon,
    label,
    detail,
    disabled
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = cx("flex min-w-0 flex-col gap-1 rounded-lg border border-border bg-background px-3 py-2.5 text-left transition-colors", "hover:border-primary/50 data-[state=checked]:border-primary data-[state=checked]:bg-accent/50 data-[state=checked]:ring-1 data-[state=checked]:ring-primary", "data-[disabled]:cursor-not-allowed data-[disabled]:border-border data-[disabled]:bg-background-secondary data-[disabled]:hover:border-border", focusRing);
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const t2 = disabled ? "text-foreground-muted" : "text-foreground";
  let t3;
  if ($[1] !== t2) {
    t3 = cx("flex items-center gap-1.5 text-body-strong", t2);
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== icon || $[4] !== label || $[5] !== t3) {
    t4 = <span className={t3}>{icon}{label}</span>;
    $[3] = icon;
    $[4] = label;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const t5 = disabled ? "text-status-warning-text" : "text-foreground-muted";
  let t6;
  if ($[7] !== t5) {
    t6 = cx("truncate text-caption", t5);
    $[7] = t5;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== detail || $[10] !== t6) {
    t7 = <span className={t6} title={detail}>{detail}</span>;
    $[9] = detail;
    $[10] = t6;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== disabled || $[13] !== label || $[14] !== t4 || $[15] !== t7 || $[16] !== value) {
    t8 = <RadioGroupPrimitives.Item value={value} disabled={disabled} aria-label={label} className={t1}>{t4}{t7}</RadioGroupPrimitives.Item>;
    $[12] = disabled;
    $[13] = label;
    $[14] = t4;
    $[15] = t7;
    $[16] = value;
    $[17] = t8;
  } else {
    t8 = $[17];
  }
  return t8;
}
interface SendFormsDialogProps {
  /** Pre-selected forms (skip form picker if provided) */
  forms?: FormTable[];
  /** Pre-selected patient (skip patient search if provided) */
  patient?: UserTiny;
  /** Links point at the appointment's form suite instead of individual forms */
  appointmentId?: string;
  /** Form IDs to pre-select in the picker when the dialog opens */
  initialFormIds?: string[];
  /**
   * Every form the appointment's form suite covers. The appointment link is
   * only used while the selection still matches this set exactly.
   */
  appointmentFormIds?: string[];
  /** Trigger element (omit for controlled-only mode) */
  children?: React.ReactNode;
  /** Controlled open state */
  open?: boolean;
  /** Controlled open state callback */
  onOpenChange?: (open: boolean) => void;
  /** Initial send tab when the dialog opens */
  initialSendMethod?: SendMethod;
  /** The plan an enrollment form is being sent for, when the caller knows it. */
  membershipId?: string;
}
export default function SendFormsDialog({
  forms: formsProp,
  patient: patientProp,
  appointmentId,
  initialFormIds,
  appointmentFormIds,
  children,
  open: controlledOpen,
  onOpenChange: controlledOnOpenChange,
  initialSendMethod = "text",
  membershipId
}: SendFormsDialogProps) {
  const [internalOpen, setInternalOpen] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState<PatientSummary | undefined>();
  const [selectedForms, setSelectedForms] = useState<FormTable[]>([]);
  const [chooseByService, setChooseByService] = useState(false);
  const [selectedServiceId, setSelectedServiceId] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [sendMethod, setSendMethod] = useState<SendMethod>(initialSendMethod);
  const [step, setStep] = useState<"select" | "compose">("select");
  const [subject, setSubject] = useState("");
  const [emailDoc, setEmailDoc] = useState<TiptapEmailJSON | null>(null);
  const [composeKey, setComposeKey] = useState(0);
  const composerRef = useRef<EmailComposerRef>(null);
  const slashCommands = useMemo(() => createSlashCommands(), []);
  const additionalExtensions = useMemo(() => [slashCommands], [slashCommands]);
  // Guards pre-selection so a refetch can't re-add forms the user removed
  const seededFormsRef = useRef(false);
  const {
    tenant
  } = useTenant();
  const {
    openChat
  } = useCommunication();
  const {
    handleDraftChange
  } = useMessageDraftStore();
  const {
    openInPersonForm
  } = useInPersonForm();
  const {
    data: emailVariables = []
  } = useSuggestions(["EMAIL", "TENANT"]);
  const variableValues = useMemo(() => Object.fromEntries(emailVariables.map(v => [v.id, v.content])), [emailVariables]);

  // Controlled / uncontrolled open state
  const isControlled = controlledOpen !== undefined;
  const isOpen = isControlled ? controlledOpen : internalOpen;
  const {
    data: headerData
  } = useEmailHeader({
    enabled: isOpen
  });
  const setIsOpen = isControlled ? (value: boolean) => controlledOnOpenChange?.(value) : setInternalOpen;

  // Show form picker only when no forms are pre-selected
  const showFormPicker = !formsProp;
  const showPatientSearch = !patientProp;

  // Fetch non-archived forms for picker (only when dialog is open and picker shown)
  const availableFormsQuery = useQuery({
    ...formQueryOptions.table(false),
    enabled: isOpen && showFormPicker
  });
  const availableServicesQuery = useQuery({
    ...serviceQueryOptions.list(),
    enabled: isOpen && showFormPicker && chooseByService
  });
  const availableForms = availableFormsQuery.data ?? [];
  const availableServices = availableServicesQuery.data ?? [];
  const availableFormById = useMemo(() => new Map(availableForms.map(form => [form.id, form])), [availableForms]);
  const selectedService = useMemo(() => availableServices.find(service => service.id === selectedServiceId), [availableServices, selectedServiceId]);
  const selectedServiceForms = useMemo(() => selectedService?.forms?.flatMap(form_0 => {
    const availableForm = availableFormById.get(form_0.id);
    return availableForm ? [availableForm] : [];
  }) ?? [], [availableFormById, selectedService]);
  const canTrustAvailableForms = !showFormPicker || availableFormsQuery.isSuccess && !availableFormsQuery.isFetching;
  const serviceQueryRequired = showFormPicker && chooseByService;
  const serviceQueryUnavailable = serviceQueryRequired && (availableServicesQuery.isPending || availableServicesQuery.isFetching || availableServicesQuery.isError);
  const activeFormsQueryUnavailable = showFormPicker && (availableFormsQuery.isPending || availableFormsQuery.isFetching || availableFormsQuery.isError);
  const unavailableServiceFormCount = useMemo(() => {
    if (!canTrustAvailableForms) return 0;
    return (selectedService?.forms ?? []).filter(form_1 => !availableFormById.has(form_1.id)).length;
  }, [availableFormById, canTrustAvailableForms, selectedService]);

  // Effective forms = prop forms, the selected service's forms, or manually selected forms.
  const forms = formsProp ?? (chooseByService ? selectedServiceForms : selectedForms);
  const patient = patientProp ?? selectedPatient;

  // A membership form renders one plan's price, and this dialog carries no plan unless
  // the caller named one — the enrolment panel and the member lists do, and nothing
  // else can. So one of them anywhere in the selection refuses the whole send rather
  // than travelling with the rest: the warning is only on screen until the click, after
  // which the toast reads as success and nothing records what did not go.
  const membershipGuard = useMembershipFormGuard(forms, isOpen && !membershipId);

  /**
   * Note who is putting these forms in front of the patient, so a form naming
   * `{provider.*}` renders their details rather than refusing.
   *
   * Fire-and-forget: the form has still been sent if this fails, so failing the send
   * over it would be the worse outcome. Fill In Person needs no row — it opens the
   * staff route, which resolves the sender from the request itself.
   *
   * Recorded when the draft is prepared, not when the text is actually sent, because
   * the SMS leaves from the chat composer and nothing carries the form ids that far.
   * So a draft that is abandoned still records — and since the resolver takes the
   * latest row, that would override an earlier real send by someone else. Accepted as
   * the lesser fault: the alternative is that no SMS send ever records one, and every
   * texted provider form refuses.
   */
  const noteAssignment = () => {
    if (!patient || forms.length === 0) return;
    void recordFormAssignment(patient.id, forms.map(form_2 => form_2.id)).catch(error => {
      // Said out loud, not just logged. The send itself has succeeded by this point, so
      // the success toast is already up — and without this the only other symptom is a
      // Form Not Ready screen the patient hits minutes later, with nothing on the staff
      // side connecting it back to this click.
      console.error("Failed to record who sent these forms:", error);
      toast({
        title: "Sent, but the sender was not recorded",
        description: "A form that fills in the sender's details will ask the patient to contact the clinic. Send it again from here to record it.",
        variant: "error"
      });
    });
  };

  // Reset state when dialog closes
  useEffect(() => {
    if (!isOpen) {
      if (!patientProp) setSelectedPatient(undefined);
      if (!formsProp) setSelectedForms([]);
      setChooseByService(false);
      setSelectedServiceId("");
      setSendMethod(initialSendMethod);
      setStep("select");
      setSubject("");
      setEmailDoc(null);
      seededFormsRef.current = false;
    }
  }, [initialSendMethod, isOpen, patientProp, formsProp]);

  // Start on a method the patient can receive, so the dialog never opens on a
  // greyed-out choice.
  useEffect(() => {
    if (!isOpen || !patient) return;
    const canText = showsPhoneNumber(patient);
    const canEmail = showsEmail(patient);
    if (sendMethod === "text" && !canText) {
      setSendMethod(canEmail ? "email" : "in_person");
    } else if (sendMethod === "email" && !canEmail) {
      setSendMethod(canText ? "text" : "in_person");
    }
  }, [isOpen, patient, sendMethod]);
  useEffect(() => {
    if (!isOpen || formsProp || seededFormsRef.current || !initialFormIds?.length || availableForms.length === 0) {
      return;
    }
    const ids = new Set(initialFormIds);
    setSelectedForms(availableForms.filter(form_3 => ids.has(form_3.id)));
    seededFormsRef.current = true;
  }, [isOpen, formsProp, initialFormIds, availableForms]);

  // The appointment link opens every form the appointment requires, so it only
  // matches the selection while the two are identical; otherwise link per form.
  const linkAppointmentId = useMemo(() => {
    if (!appointmentId || !appointmentFormIds?.length) return appointmentId;
    const selectedIds = new Set(forms.map(form_4 => form_4.id));
    const coversAppointment = selectedIds.size === new Set(appointmentFormIds).size && appointmentFormIds.every(id => selectedIds.has(id));
    return coversAppointment ? appointmentId : undefined;
  }, [appointmentId, appointmentFormIds, forms]);

  // --- Text send ---
  const handleSendText = async () => {
    if (forms.length === 0 || !patient) return;
    if (!showsPhoneNumber(patient)) {
      toast({
        title: "Unable to Send Forms",
        description: "This patient needs a phone number to receive text messages.",
        variant: "error"
      });
      return;
    }
    setIsLoading(true);
    try {
      const formsMessage = generateMultipleFormsMessage(patient, forms, tenant?.subDomain, linkAppointmentId, membershipId);
      await openChat(patient.id, undefined, (chatId: string) => {
        handleDraftChange(chatId, formsMessage, {
          kind: MessageKind.DEFAULT
        });
      });

      // After the draft is up, matching the email route: a send that fell over leaves
      // no row claiming this provider sent the form.
      noteAssignment();
      toast({
        title: "Chat Opened",
        description: "Forms message has been prepared. Review and send when ready.",
        variant: "success"
      });
      setIsOpen(false);
    } catch (error_0) {
      console.error("Error opening chat:", error_0);
      const errorMessage = await parseError(error_0);
      toast({
        title: "Unable to Open Chat",
        description: errorMessage || "Failed to open chat. Please check your permissions and try again.",
        variant: "error"
      });
    } finally {
      setIsLoading(false);
    }
  };

  // --- Email compose (step 1 → step 2) ---
  const handleComposeEmail = () => {
    if (forms.length === 0 || !patient) return;
    if (!showsEmail(patient)) {
      toast({
        title: "No email address",
        description: "This patient does not have an email address on file.",
        variant: "error"
      });
      return;
    }
    const buttonColor = tenant?.brandPrimaryColor ?? "#4a5d4e";
    const doc = buildFormsEmailDocument(headerData?.templateJson, patient, forms, tenant?.subDomain, buttonColor, linkAppointmentId, {
      clinicName: variableValues["clinic_name"],
      logoUrl: variableValues["clinic_logo"]
    }, membershipId);
    setSubject(forms.length === 1 ? `Please fill out: ${forms[0].name}` : `Please fill out ${forms.length} forms`);
    setEmailDoc(doc);
    setComposeKey(k => k + 1);
    setStep("compose");
  };

  // --- Email send (step 2) ---
  const handleSendComposedEmail = async () => {
    if (!patient || !composerRef.current) return;
    setIsLoading(true);
    try {
      const bodyHtml = await composerRef.current.getHtml(variableValues);
      await sendFormEmail({
        patientId: patient.id,
        formIds: forms.map(f => f.id),
        bodyHtml,
        subject
      });
      toast({
        title: "Email sent",
        description: `Form${forms.length === 1 ? "" : "s"} sent to ${patient.email ?? patient.firstName ?? "the patient"}`,
        variant: "success"
      });
      setIsOpen(false);
    } catch (error_1) {
      console.error("Error sending form email:", error_1);
      toast({
        title: "Failed to send email",
        description: "Please try again.",
        variant: "error"
      });
    } finally {
      setIsLoading(false);
    }
  };
  const handleBack = () => {
    setStep("select");
    setEmailDoc(null);
  };

  // --- Fill in person ---
  const handleFillInPerson = () => {
    if (!patient || forms.length === 0) return;
    openInPersonForm(patient.id, forms, membershipId);
    setIsOpen(false);
  };
  const handleCancel = () => {
    setIsOpen(false);
  };
  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
  };

  // isPending is load-bearing on its own: picking a form and clicking Send is one
  // motion, and in the few hundred ms before the probe answers a membership form reads
  // as ordinary.
  const serviceSelectionBlocked = chooseByService && (serviceQueryUnavailable || activeFormsQueryUnavailable || !selectedService || selectedServiceForms.length === 0 || unavailableServiceFormCount > 0);
  const canSend = !!patient && forms.length > 0 && !serviceSelectionBlocked && !membershipGuard.isPending && !membershipGuard.checkFailed && membershipGuard.blockedFormIds.length === 0;
  // The API blanks contact details for roles without patients:contact_info:read,
  // but both sends go by patient id, so only a truly missing one blocks them.
  const noEmail = sendMethod === "email" && patient && !showsEmail(patient);
  const noPhone = sendMethod === "text" && patient && !showsPhoneNumber(patient);

  // Says why the send button is disabled, beside the button, for the gates nothing
  // else on screen explains. Load failures, a service with no or archived forms and
  // membership forms already have their own message next to the picker.
  const disabledReason = (() => {
    if (!patient) return "Select a patient to send forms.";
    if (noEmail) return "This patient does not have an email address on file.";
    if (noPhone) return "This patient does not have a phone number on file.";
    if (showFormPicker && chooseByService && !selectedService && !serviceQueryUnavailable) {
      return "Choose a service to send its forms.";
    }
    if (showFormPicker && !chooseByService && forms.length === 0 && availableForms.length > 0) {
      return "Select at least one form to send.";
    }
    if (forms.length > 0 && membershipGuard.isPending) {
      return "Checking the selected forms...";
    }
    return null;
  })();
  return <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      {children && <DialogTrigger asChild>{children}</DialogTrigger>}
      <DialogContent className={step === "select" ? "sm:max-w-xl" : "sm:max-w-2xl"} onInteractOutside={keepOpenWhileDropdownOpen} onEscapeKeyDown={closeDropdownOnEscape}>
        {step === "select" ? <DialogHeader>
            <DialogTitle>
              {patient ? `Send forms to ${[patient.firstName, patient.lastName].filter(Boolean).join(" ")}` : "Send forms"}
            </DialogTitle>
            <DialogDescription className="text-body">
              Pick the forms, then how the patient should get them.
            </DialogDescription>
          </DialogHeader> : <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Button variant="ghost" onClick={handleBack} className="rounded-md p-1 hover:bg-hover" icon={<IoArrowBack size={18} />} size="icon-sm" />
              Compose Email
            </DialogTitle>
          </DialogHeader>}

        {step === "select" ? <DialogBody>
            <div className="space-y-5">
              {/* Patient search (when no patient prop) */}
              {showPatientSearch && <div>
                  <Label className="text-body-strong">Patient</Label>
                  <div className="mt-2">
                    <PatientSearch patient={selectedPatient} onPatientSelect={setSelectedPatient} placeholder="Search for a patient..." />
                  </div>
                </div>}

              {/* Pre-selected forms list */}
              {formsProp && formsProp.length > 0 && <div>
                  <Label className="text-body-strong">
                    {`Forms (${formsProp.length})`}
                  </Label>
                  <ul className="mt-2 divide-y divide-border rounded-lg border border-border">
                    {formsProp.map(form_5 => <li key={form_5.id} className={cx("flex items-center gap-2 px-3 py-2 text-body", membershipGuard.blockedFormIds.includes(form_5.id) ? "text-status-warning-text" : "text-foreground")}>
                        <LuFileText className="h-4 w-4 shrink-0 text-foreground-muted" />
                        <span className="min-w-0 truncate">{form_5.name}</span>
                      </li>)}
                  </ul>
                </div>}

              {/* Form or service picker (when no forms prop) */}
              {showFormPicker && <div className="space-y-2">
                  <div className="flex items-center justify-between gap-3">
                    <Label className="text-body-strong">
                      {chooseByService ? "Service" : forms.length > 0 ? `Forms (${forms.length})` : "Forms"}
                    </Label>
                    <div className="flex items-center gap-2">
                      <Label htmlFor="choose-forms-by-service" className="cursor-pointer text-body text-foreground-secondary">
                        Choose by service
                      </Label>
                      <Switch id="choose-forms-by-service" size="small" checked={chooseByService} onCheckedChange={setChooseByService} />
                    </div>
                  </div>

                  {chooseByService ? <>
                      <ServiceSearchSelect services={availableServices} value={selectedServiceId} onValueChange={setSelectedServiceId} placeholder="Choose a service..." searchPlaceholder="Search services..." emptyMessage={availableServicesQuery.isPending || availableServicesQuery.isFetching ? "Loading services..." : "No services found"} disabled={availableServicesQuery.isPending || availableServicesQuery.isFetching || availableServicesQuery.isError} />

                      {(availableServicesQuery.isPending || availableServicesQuery.isFetching) && <p className="text-body text-foreground-secondary">
                          Loading services...
                        </p>}

                      {availableServicesQuery.isError && <p className="text-body text-status-warning-text">
                          Services could not be loaded. Try again before sending
                          forms by service.
                        </p>}

                      {(availableFormsQuery.isPending || availableFormsQuery.isFetching) && <p className="text-body text-foreground-secondary">
                          Checking active forms for this service...
                        </p>}

                      {availableFormsQuery.isError && <p className="text-body text-status-warning-text">
                          Active forms could not be loaded. Try again before
                          sending forms by service.
                        </p>}

                      {selectedService && selectedServiceForms.length > 0 && <div className="text-body text-foreground-secondary">
                          <p>
                            <span>{`Includes ${selectedServiceForms.length} form${selectedServiceForms.length === 1 ? "" : "s"}:`}</span>
                          </p>
                          <ul className="mt-1 list-disc space-y-0.5 pl-5">
                            {selectedServiceForms.map(form_6 => <li key={form_6.id}>{form_6.name}</li>)}
                          </ul>
                        </div>}

                      {selectedService && (selectedService.forms?.length ?? 0) === 0 && <p className="text-body text-status-warning-text">
                            This service has no forms assigned.
                          </p>}

                      {selectedService && canTrustAvailableForms && unavailableServiceFormCount > 0 && <p className="text-body text-status-warning-text">
                            Some forms assigned to this service are archived or
                            unavailable. Update the service before sending its
                            forms.
                          </p>}
                    </> : <MultiSelect options={availableForms.map(f_0 => ({
              value: f_0.id,
              label: f_0.name
            }))} value={selectedForms.map(f_1 => f_1.id)} onChange={values => {
              setSelectedForms(availableForms.filter(f_2 => values.includes(f_2.id)));
            }}>
                      <MultiSelectTrigger placeholder="Search and select forms..." />
                      <MultiSelectContent>
                        {availableForms.map(form_7 => <MultiSelectItem key={form_7.id} value={form_7.id}>
                            {form_7.name}
                          </MultiSelectItem>)}
                      </MultiSelectContent>
                      <MultiSelectPills />
                    </MultiSelect>}
                </div>}

              {showFormPicker && !chooseByService && (availableFormsQuery.isPending || availableFormsQuery.isFetching) && <p className="text-body text-foreground-secondary">
                    Loading forms...
                  </p>}

              {showFormPicker && !chooseByService && availableFormsQuery.isError && <p className="text-body text-status-warning-text">
                    Forms could not be loaded. Try again before sending.
                  </p>}

              {showFormPicker && !chooseByService && availableFormsQuery.isSuccess && !availableFormsQuery.isFetching && availableForms.length === 0 && <p className="text-body text-foreground-secondary">
                    No forms available to send.
                  </p>}

              {membershipGuard.blockedReason && <div className="flex items-start gap-2 rounded-md border border-border bg-background-secondary p-3 text-body text-foreground-secondary">
                  <RiErrorWarningLine className="mt-0.5 shrink-0 text-status-warning-text" />
                  <span>{membershipGuard.blockedReason}</span>
                </div>}

              {/* Delivery method */}
              <div className="space-y-2">
                <Label id="send-forms-method" className="text-body-strong">
                  Send by
                </Label>
                <RadioGroupPrimitives.Root aria-labelledby="send-forms-method" value={sendMethod} onValueChange={v_0 => setSendMethod(v_0 as SendMethod)} className="grid grid-cols-1 gap-2 sm:grid-cols-3">
                  <DeliveryOption value="text" icon={<LuMessageSquare className="h-4 w-4" />} label="Text" detail={!patient ? "Text a link" : showsPhoneNumber(patient) ? formatPhoneNumberOrHidden(patient) : "No phone on file"} disabled={!!patient && !showsPhoneNumber(patient)} />
                  <DeliveryOption value="email" icon={<LuMail className="h-4 w-4" />} label="Email" detail={!patient ? "Email a link" : showsEmail(patient) ? formatEmailOrHidden(patient) : "No email on file"} disabled={!!patient && !showsEmail(patient)} />
                  <DeliveryOption value="in_person" icon={<LuClipboardList className="h-4 w-4" />} label="In person" detail="Fill on this device" />
                </RadioGroupPrimitives.Root>
              </div>
            </div>
          </DialogBody> : <DialogBody>
            <div className="space-y-4">
              <div className="space-y-1">
                <Label className="text-body-strong">Subject</Label>
                <Input value={subject} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSubject(e.target.value)} placeholder="Email subject" />
              </div>

              {patient && <p className="text-body text-foreground-secondary">
                  {`To: ${patient.firstName} ${patient.lastName}${patient.email ? ` (${patient.email})` : ""}`}
                </p>}

              {emailDoc && <EmailComposer mode="block" key={composeKey} ref={composerRef} initialContent={emailDoc} variables={emailVariables} additionalExtensions={additionalExtensions} resolveOnInsert styleSettings={{
            maxWidth: 424
          }}>
                  <EmailComposer.Toolbar />
                  <div className="flex justify-end">
                    <EmailComposer.LoadTemplate onSelect={t => setSubject(t.subject)} />
                  </div>
                  <EmailComposer.Canvas className="min-h-48" />
                  <EmailComposer.LinkBubbleMenu />
                  <EmailComposer.ImageBubbleMenu />
                  <EmailComposer.ButtonBubbleMenu />
                </EmailComposer>}
            </div>
          </DialogBody>}

        {step === "select" ? <DialogFooter>
            {disabledReason && <p className="mr-auto self-center text-body text-foreground-muted">
                {disabledReason}
              </p>}
            <Button variant="ghost" onClick={handleCancel}>
              Cancel
            </Button>
            {sendMethod === "text" && <Button variant="primary" onClick={handleSendText} disabled={!canSend || isLoading || !!noPhone} tooltip={disabledReason} isLoading={isLoading}>
                Open in chat
              </Button>}
            {sendMethod === "email" && <Button variant="primary" onClick={handleComposeEmail} disabled={!canSend || !!noEmail} tooltip={disabledReason}>
                Write email
              </Button>}
            {sendMethod === "in_person" && <Button variant="primary" onClick={handleFillInPerson} disabled={!canSend || isLoading} tooltip={disabledReason}>
                Fill in person
              </Button>}
          </DialogFooter> : <DialogFooter>
            <Button variant="secondary" onClick={handleBack}>
              Back
            </Button>
            <Button variant="primary" onClick={handleSendComposedEmail} disabled={!subject.trim() || isLoading} isLoading={isLoading}>
              Send Email
            </Button>
          </DialogFooter>}
      </DialogContent>
    </Dialog>;
}